import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { DateRangeInterface, DSSetup, Service, ValidationErrors } from 'webapp-types';
import { Unit } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { isAlive } from 'webapp/utils/common';
import type { ValidationFieldsConfig } from 'webapp/utils/validate';
import { validateForm } from 'webapp/utils/validate';

import BarLoaderComponent from 'webapp/components/bar-loader';
import ClientMetricsSetupFormComponent from 'webapp/components/client/metrics/setup-form';
import type { OptionRaw } from 'webapp/components/form/select';
import FormSelectComponent from 'webapp/components/form/select';
import RowErrorComponent from 'webapp/components/row-error';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { MetricRecord } from 'webapp/models';
import type { ClientMetric, MetricSetupForClient } from 'webapp/models-custom';

import { Severity } from 'webapp/services/logger';

interface Args {
	model: ClientMetric | null;
	spaceId: number;
	position: number;
	onlySourceOfType?: string;
	hideOptionToSelectDimensions?: boolean;
	onClose: () => void;
	onRecenter: () => void;
}

export interface ClientMetricData extends Omit<MetricRecord, 'dimensions'> {
	key: string;
	title: string;
	dimensions: DSSetup.DimensionItem[];
}

export interface ClientSource {
	id: number;
	avatar: string;
	title: string;
	subtitle: string;
	type: string;
	owner: {
		id: number;
	};
	metrics: ClientMetricData[];
}

export default class ClientMetricsEditComponent extends Component<Args> {
	@service('request') declare requestService: Service.Request;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/client-metrics')
	declare mmClientMetricsService: Service.ModelManager.ClientMetrics;

	@tracked isLoading = false;
	@tracked errors: ValidationErrors<string> = {};
	@tracked clientSourceData: ClientSource[] | null = null;
	@tracked submitInProgress = false;
	@tracked selectedSource: string | null = null;
	@tracked selectedDateRange: DateRangeInterface.FormulaInterface | null = null;
	@tracked model: ClientMetric;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.fetchItemTree();
		const model = this.args.model;

		if (model) {
			this.model = model;
			this.selectedDateRange = model.dateRange;
		} else {
			this.model = this.mmClientMetricsService.createNewModel();
		}
	}

	get metricSetup(): MetricSetupForClient {
		return this.model.metricSetup;
	}

	get isFromThisSpace(): boolean {
		return this.args.spaceId === this.accountService.id;
	}

	get columnClientPerformanceMetrics(): ClientMetric[] {
		return this.mmClientMetricsService.clientMetricsModels.filter(
			(item) => item.order === this.args.position,
		);
	}

	get showClearColumnBtn(): boolean {
		return !!this.columnClientPerformanceMetrics.length;
	}

	get availableSourcesOfType(): OptionRaw<string>[] {
		const type = this.args.onlySourceOfType,
			sources: OptionRaw<string>[] = [];

		if (type) {
			this.clientSourceData?.forEach((source) => {
				if (source.type === type) {
					sources.push({
						value: source.id.toString(),
						label: source.title || '',
						label2: source.subtitle || '',
						avatar: source.avatar || '',
					});
				}
			});
		}
		return sources;
	}

	@action
	resetErrors() {
		this.errors = {};
	}

	@action
	save() {
		const model = this.model;

		const isMain = this.isFromThisSpace;
		if (!this.submitInProgress && this.validate()) {
			const isEdit = !model.isNew;

			if (this.args.onlySourceOfType && this.selectedSource) {
				model.metricSetupSpaceAccessId = parseInt(this.selectedSource);
			} else {
				model.spaceId = this.args.spaceId;
				model.order = this.args.position;

				const selectedDateRange = this.selectedDateRange;

				if (selectedDateRange) {
					model.dateRange = selectedDateRange;
				}
			}

			this.submitInProgress = true;
			const layer = this.infolayerService.loading('Saving metric...');

			model
				.save()
				.then(
					() => {
						layer.ok(`Metric ${isEdit ? 'updated' : 'added'}!`);
						this.close();
					},
					(errorData) => {
						layer.close();

						const model = this.model;
						if (model) {
							void model.delete(true);
						}
						throw errorData;
					},
				)
				.finally(() => {
					if (isMain) {
						this.analyticsService.event(
							isEdit
								? TrackingEvent.CLIENT_KPI_COLUMN_CHANGED
								: TrackingEvent.CLIENT_KPI_COLUMN_SET,
						);
					} else if (this.args.onlySourceOfType) {
						this.analyticsService.event(TrackingEvent.CLIENT_KPI_TILE_SELECT_SOURCE);
					} else {
						this.analyticsService.event(TrackingEvent.CLIENT_KPI_TILE_EDIT);
					}
					if (isAlive(this)) {
						this.submitInProgress = false;
						next(() => {
							this.close();
						});
					}
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}
	}

	@action
	close() {
		if (!this.submitInProgress) {
			this.args.onClose();
		}
	}

	@action
	changeSelectedSource(sourceId: string | null) {
		this.selectedSource = sourceId;
	}

	@action
	updateDateRange(dateRangeFormula: DateRangeInterface.FormulaInterface | null) {
		this.selectedDateRange = dateRangeFormula;
	}

	@action
	clearColumnClientPerformanceMetrics() {
		const infolayer = this.infolayerService.loading('Clearing column...');
		this.submitInProgress = true;

		//BE automatically deletes all metrics in this column when the ID is associated with an agency
		this.requestService
			.api('DELETE', `/client-metrics/${this.columnClientPerformanceMetrics[0]!.id}`)
			.then(
				() => {
					infolayer.ok('Column has been successfully cleared!');
				},
				(errorData) => {
					this.loggerService.logError(
						`[Clear Column Client Performance Metrics] DELETE failed`,
						{
							errors: JSON.stringify(errorData),
						},
						Severity.ERROR,
					);

					infolayer.close();
					throw errorData;
				},
			)
			.finally(() => {
				this.submitInProgress = false;
				this.close();
			});
	}

	private fetchItemTree(): void {
		if (!this.isFromThisSpace) {
			this.isLoading = true;
			this.args.onRecenter();

			this.requestService
				.api<ClientSource[]>('GET', `/client-metrics/keys/${this.args.spaceId}`)
				.then((reply) => {
					this.clientSourceData = reply; // BE will skip generic metrics (script / queries)
				})
				.finally(() => {
					this.isLoading = false;
					this.args.onRecenter();
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}
	}

	private validate(): boolean {
		const rules: ValidationFieldsConfig = {},
			metricSetup = this.metricSetup;

		const values: Record<string, unknown> = {
			selectedMetric: this.model.metricSetupKey,
			selectedDateRange: this.selectedDateRange,
		};

		if (metricSetup.allowBasicDimensionValueFilters) {
			values.selectedAttribute = metricSetup.firstAttributeNameFromBasicFilters;
		}

		if (metricSetup.hasUnits) {
			values.unit = metricSetup.selectedUnit;
		}

		if (this.args.onlySourceOfType) {
			values.selectedSource = this.selectedSource;
			rules.selectedSource = ['required'];
		} else {
			rules.selectedMetric = ['required'];
			rules.selectedDateRange = ['required'];

			if (
				metricSetup.allowBasicDimensionValueFilters &&
				!metricSetup.firstAttributeNameFromBasicFilters
			) {
				rules.selectedAttribute = ['required'];
			}

			if (
				metricSetup.hasUnits &&
				(!metricSetup.selectedUnit || metricSetup.selectedUnit === Unit.ALL)
			) {
				rules.unit = ['required'];
			}
		}

		const errors = validateForm(values, rules);

		if (!this.selectedDateRange?.range) {
			errors.dateRange = [{ message: 'Select a valid date range' }];
		}

		this.errors = errors;
		return Object.keys(this.errors).length === 0;
	}
	<template>
		{{#if this.isLoading}}
			<BarLoaderComponent @size="big" />
		{{else}}

			{{#if @onlySourceOfType}}
				<FormSelectComponent
					name="source"
					@selected={{this.selectedSource}}
					@options={{this.availableSourcesOfType}}
					@onChange={{this.changeSelectedSource}}
					@isNumeric={{false}}
					@placeholder="Data source"
					@isDisabled={{this.submitInProgress}}
					@isRequired={{true}}
				/>
				<RowErrorComponent @items={{this.errors.selectedSource}} />
			{{else}}
				<ClientMetricsSetupFormComponent
					@data={{this.clientSourceData}}
					@spaceId={{@spaceId}}
					@errors={{this.errors}}
					@isFromThisSpace={{this.isFromThisSpace}}
					@submitInProgress={{this.submitInProgress}}
					@selectedDateRange={{this.selectedDateRange}}
					@onUpdateDateRange={{this.updateDateRange}}
					@onRecenter={{@onRecenter}}
					@metricSetup={{this.metricSetup}}
					@hideOptionToSelectDimensions={{@hideOptionToSelectDimensions}}
					@onResetErrors={{this.resetErrors}}
				/>
			{{/if}}

			<div class="controls2">
				{{#if this.showClearColumnBtn}}
					<div class="controls2__left">
						<SubmitButtonComponent
							@text="Clear column"
							@theme="red-outlined"
							@noDelay={{true}}
							@onTrigger={{this.clearColumnClientPerformanceMetrics}}
						/>
					</div>
				{{/if}}
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
					/>
				</div>
			</div>
		{{/if}}
	</template>
}
